import { queryClient } from 'lib/queryClient'

import { accessControlApi } from '../pages/AccessControl/api/access-control.client'

import { can } from './ability'
import type { RbacAction, RbacSubject } from './constants'
import { isRbacEnforcementEnabled } from './enforcement-flag'
import { MY_ABILITY_QUERY_KEY } from './useMyAbilityQuery'
import { navigationProgress } from './navigation-progress'

interface AbilityOnEnterArgs {
  action: RbacAction
  subject: RbacSubject
  // react-router-3 `replace` from the route's onEnter. On a denial we redirect
  // to Home rather than calling back with a 401 — matching adminShellOnEnter, so
  // a finer-permission miss lands on the in-app "no access" Home experience
  // instead of the 404 page the router's error handler produces for a 401.
  replace: (pathname: string) => void
  callback: (error?: { status: number; message: string }) => void
}

/**
 * Route-level ability gate, mirroring authOnEnter but checking DB-driven
 * ability instead of a hardcoded Cognito role. Resolves the current user's
 * rules from the shared React Query cache (fetching once if absent), then allows
 * or blocks the route. Compose AFTER authOnEnter so the coarse role check runs
 * first.
 *
 * On a denial (or a fail-closed resolution error) it REPLACES to Home — the same
 * behavior as adminShellOnEnter — so the user gets the in-app no-access flow
 * (Home / NoAdminAccess), not the router's 404. UX only — the EPS AbilityGuard is
 * the authoritative boundary.
 */
export async function abilityOnEnter({
  action,
  subject,
  replace,
  callback,
}: AbilityOnEnterArgs): Promise<void> {
  // Kill-switch: when RBAC enforcement is OFF (or LD unreachable — fail-safe),
  // this gate is a no-op and the route falls back to its coarse role check,
  // matching the EPS server. Checked before any fetch so a disabled feature adds
  // no latency and can never lock an admin out.
  if (!(await isRbacEnforcementEnabled())) {
    callback()
    return
  }
  // The fetch below blocks the route transition; show the nav progress bar so
  // the click visibly responds instead of feeling dead.
  navigationProgress.start()
  try {
    // staleTime: 0 — re-check against the server on every route entry so a
    // permission change reflects on the next navigation rather than after the
    // cache expires.
    const data = await queryClient.fetchQuery({
      queryKey: MY_ABILITY_QUERY_KEY,
      queryFn: () => accessControlApi.getMyAbility(),
      staleTime: 0,
    })
    if (can(data?.rules, action, subject)) {
      callback()
      return
    }
    // Lacks the finer permission — redirect to Home (in-app no-access flow),
    // consistent with adminShellOnEnter, instead of a 401 -> 404.
    replace('/')
    callback()
  } catch {
    // Fail-closed on a resolution error: block by redirecting to Home.
    replace('/')
    callback()
  } finally {
    navigationProgress.stop()
  }
}
